import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, NavLink } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import Spinner from "react-bootstrap/Spinner";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FeeSystemMenu from "./FeeSystemMenu";
import RightsCheck from "../General/RightsCheck";
import { scrollToTop } from "../Shared/ScrollOnTop";
import config from "../../Config";

const CHALLAN_EDIT_MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

/** Calendar years for dropdown: current ± a few, always including <paramref name="anchorYear" /> when valid. */
function challanEditYearRange(anchorYear) {
  const center = new Date().getFullYear();
  const a =
    Number.isFinite(anchorYear) && anchorYear >= 1990 && anchorYear <= 2100
      ? anchorYear
      : center;
  const low = Math.min(center - 5, a - 2);
  const high = Math.max(center + 3, a + 2);
  const years = [];
  for (let y = low; y <= high; y++) years.push(y);
  return years;
}

function parseHeadMonthYear(head) {
  const rawM = head && (head.Month ?? head.month);
  const rawY = head && (head.Year ?? head.year);
  const m = parseInt(rawM, 10);
  const y = parseInt(rawY, 10);
  const month = m >= 1 && m <= 12 ? m : 1;
  const year =
    Number.isFinite(y) && y >= 1990 && y <= 2100 ? y : new Date().getFullYear();
  return { month, year };
}

function cloneLines(lines) {
  if (!lines || !lines.length) return [];
  return lines.map((r) => ({
    ...r,
    DeleteLine: false,
  }));
}

function emptyNewLine() {
  return {
    IsNew: true,
    ClientKey: `n-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    ID: 0,
    Student_ID: 0,
    PostingDate: null,
    FeeCode: "",
    Description: "",
    Fee_Posted: 0,
    Arrears: 0,
    ConcessionAmount: 0,
    ReceiveableFee: 0,
    PendingFee: 0,
    ReceivedAmount: 0,
    ReceiptRef: null,
    DisplayDebit: 0,
    DeleteLine: false,
  };
}

/** Calendar date for <input type="date"> (YYYY-MM-DD). Prefers the literal YYYY-MM-DD from the wire to avoid timezone day shifts. */
function toInputDate(v) {
  if (v == null || v === "") return "";
  if (typeof v === "string") {
    const isoDay = v.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDay) return isoDay[1];
    const ms = v.match(/\/Date\((-?\d+)\)\//);
    if (ms) {
      const d = new Date(parseInt(ms[1], 10));
      if (!Number.isNaN(d.getTime())) return moment(d).format("YYYY-MM-DD");
    }
  }
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return moment(v).format("YYYY-MM-DD");
  }
  const m = moment(v);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
}

/** Read first defined property (including false/0). */
function pickHeadField(head, names) {
  if (!head) return undefined;
  for (const name of names) {
    if (Object.prototype.hasOwnProperty.call(head, name)) {
      const val = head[name];
      if (val !== undefined && val !== null) return val;
    }
  }
  return undefined;
}

function dueDateFromHead(head) {
  return toInputDate(
    pickHeadField(head, [
      "Due_Date",
      "due_Date",
      "dueDate",
      "DueDate",
    ])
  );
}

function afterDueDateFromHead(head) {
  return toInputDate(
    pickHeadField(head, [
      "After_Due_Date",
      "after_Due_Date",
      "afterDueDate",
      "AfterDueDate",
    ])
  );
}

/** Merge camelCase / PascalCase API shapes into stable PascalCase fields used by this screen. */
function normalizeChallanHead(raw) {
  if (!raw) return null;
  const p = (names) => pickHeadField(raw, names);
  return {
    ...raw,
    ID: p(["ID", "id"]) ?? raw.ID,
    Challan_No: p(["Challan_No", "challan_No"]) ?? raw.Challan_No,
    Student_Name: p(["Student_Name", "student_Name"]) ?? raw.Student_Name,
    Registration_No: p(["Registration_No", "registration_No"]) ?? raw.Registration_No,
    Month: p(["Month", "month"]) ?? raw.Month,
    Year: p(["Year", "year"]) ?? raw.Year,
    ChallanType: p(["ChallanType", "challanType"]) ?? raw.ChallanType,
    Is_Paid: p(["Is_Paid", "is_Paid"]) ?? raw.Is_Paid ?? false,
    Due_Date_Amount: p(["Due_Date_Amount", "due_Date_Amount"]) ?? raw.Due_Date_Amount,
    After_Due_Date_Amount: p(["After_Due_Date_Amount", "after_Due_Date_Amount"]) ?? raw.After_Due_Date_Amount,
    Due_Date: p(["Due_Date", "due_Date", "dueDate", "DueDate"]) ?? raw.Due_Date,
    After_Due_Date: p(["After_Due_Date", "after_Due_Date", "afterDueDate", "AfterDueDate"]) ?? raw.After_Due_Date,
  };
}

function pickResponseHead(data) {
  if (!data) return null;
  return data.Head ?? data.head ?? null;
}

function pickResponseLedgerLines(data) {
  if (!data) return [];
  return data.LedgerLines ?? data.ledgerLines ?? [];
}

function pickChalanHeadId(data, head) {
  if (!data) return 0;
  const id = data.ChalanHead_Id ?? data.chalanHead_Id;
  if (id != null && id !== "") return id;
  const hid = head && (head.ID ?? head.id);
  return hid != null ? hid : 0;
}

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

/** Sum Fee posted, Arrears, Concession, Receivable, Received, Pending across AD lines. */
function sumLedgerLineColumns(lines, options = {}) {
  const excludeDeleted = options.excludeDeleted === true;
  const acc = {
    feePosted: 0,
    arrears: 0,
    concession: 0,
    receivable: 0,
    received: 0,
    pending: 0,
  };
  for (const row of lines || []) {
    if (excludeDeleted && row.DeleteLine) continue;
    acc.feePosted += num(row.Fee_Posted);
    acc.arrears += num(row.Arrears);
    acc.concession += num(row.ConcessionAmount);
    acc.receivable += num(row.ReceiveableFee);
    acc.received += num(row.ReceivedAmount);
    acc.pending += num(row.PendingFee);
  }
  return acc;
}

function fmtSum(n) {
  return num(n).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function rowKey(row) {
  return row.ClientKey || row.ID;
}

function ChallanEdit() {
  const { chalanHeadId: chalanHeadIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const studentIdQ = parseInt(searchParams.get("studentId") || "0", 10) || 0;
  const challanTypeQ = (searchParams.get("type") || "").trim();

  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [error, setError] = useState("");
  const [saveNotice, setSaveNotice] = useState("");
  const [head, setHead] = useState(null);
  const [readonlyLines, setReadonlyLines] = useState([]);
  const [editLines, setEditLines] = useState([]);
  const [resolvedHeadId, setResolvedHeadId] = useState(0);
  const [feeTypes, setFeeTypes] = useState([]);

  const [dueDate, setDueDate] = useState("");
  const [afterDueDate, setAfterDueDate] = useState("");
  const [editMonth, setEditMonth] = useState(1);
  const [editYear, setEditYear] = useState(() => new Date().getFullYear());

  const readonlyTotals = sumLedgerLineColumns(readonlyLines);
  const editTotals = sumLedgerLineColumns(editLines, { excludeDeleted: true });

  const loadPayload = useCallback(() => {
    const idFromRoute = parseInt(chalanHeadIdParam || "0", 10) || 0;
    return {
      AccessKey,
      UserID: parseInt(UserID, 10) || 0,
      MemberType: MemberType || "Admin",
      ChalanHead_Id: idFromRoute,
      Challan_No: "",
      Student_ID: studentIdQ,
      Challan_Type: challanTypeQ,
    };
  }, [AccessKey, UserID, MemberType, chalanHeadIdParam, studentIdQ, challanTypeQ]);

  const fetchChallan = useCallback(() => {
    setLoading(true);
    setError("");
    setSaveNotice("");
    axios({
      method: "post",
      url: config.base_url + "ChallanFeeSystem/GetChalanForEdit",
      headers: { "Content-Type": "application/json" },
      data: loadPayload(),
      timeout: 120000,
    })
      .then((res) => {
        if (res.data.status_code !== 1) {
          setHead(null);
          setReadonlyLines([]);
          setEditLines([]);
          setResolvedHeadId(0);
          setError(res.data.status_message || "Could not load challan.");
          return;
        }
        const hRaw = pickResponseHead(res.data);
        const h = normalizeChallanHead(hRaw);
        const lines = pickResponseLedgerLines(res.data);
        setHead(h);
        setResolvedHeadId(pickChalanHeadId(res.data, h));
        setReadonlyLines(lines);
        setEditLines(cloneLines(lines));
        setDueDate(dueDateFromHead(h));
        setAfterDueDate(afterDueDateFromHead(h));
        const myLoad = parseHeadMonthYear(h);
        setEditMonth(myLoad.month);
        setEditYear(myLoad.year);
      })
      .catch(() => {
        setError("Network error while loading challan.");
        setHead(null);
        setReadonlyLines([]);
        setEditLines([]);
      })
      .finally(() => setLoading(false));
  }, [loadPayload]);

  useEffect(() => {
    document.title = "Edit challan";
  }, []);

  useEffect(() => {
    fetchChallan();
  }, [fetchChallan]);

  useEffect(() => {
    if (!head) {
      setFeeTypes([]);
      return;
    }
    axios({
      method: "post",
      url: config.base_url + "FeeType/GetFeeTypes",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID: parseInt(UserID, 10) || 0,
        MemberType: MemberType || "Admin",
        pageNo: 1,
        pageSize: 500,
        SchoolID: 1,
      },
      timeout: 60000,
    })
      .then((res) => {
        if (res.data.status_code === 1 && Array.isArray(res.data.ListofRecords)) {
          setFeeTypes(res.data.ListofRecords);
        } else {
          setFeeTypes([]);
        }
      })
      .catch(() => setFeeTypes([]));
  }, [head, AccessKey, UserID, MemberType]);

  useEffect(() => {
    if (!saveNotice) return undefined;
    const t = setTimeout(() => setSaveNotice(""), 8000);
    return () => clearTimeout(t);
  }, [saveNotice]);

  const updateLine = (idx, field, value) => {
    setEditLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const applyFeeTypeToRow = (idx, feeTypeId) => {
    const id = parseInt(feeTypeId, 10);
    if (!id || Number.isNaN(id)) return;
    const ft = feeTypes.find((x) => x.ID === id || x.Fee_Type_ID === id);
    if (!ft) return;
    setEditLines((prev) => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        FeeCode: ft.Fee_Type_Code || "",
        Description: ft.Fee_Type_Name || "",
      };
      return next;
    });
  };

  const addFeeLine = () => {
    setEditLines((prev) => [...prev, emptyNewLine()]);
  };

  const removeUnsavedLine = (idx) => {
    setEditLines((prev) => {
      if (!prev[idx]?.IsNew) return prev;
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!resolvedHeadId || !head) return;
    if (head.Is_Paid) {
      const msg = "This challan is paid and cannot be edited.";
      setError(msg);
      toast.error(msg, { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    setSaving(true);
    setError("");
    setSaveNotice("");
    const lines = editLines.map((row) => ({
      LedgerId: row.IsNew ? 0 : row.ID,
      DeleteLine: !!row.DeleteLine,
      Fee_Posted: num(row.Fee_Posted),
      Arrears: num(row.Arrears),
      ConcessionAmount: num(row.ConcessionAmount),
      ReceiveableFee: num(row.ReceiveableFee),
      PendingFee: num(row.PendingFee),
      Description: row.Description ?? "",
      FeeCode: row.FeeCode ?? "",
    }));
    const data = {
      AccessKey,
      UserID: parseInt(UserID, 10) || 0,
      MemberType: MemberType || "Admin",
      ChalanHead_Id: resolvedHeadId,
      Due_Date: dueDate ? `${dueDate}T12:00:00` : null,
      After_Due_Date: afterDueDate ? `${afterDueDate}T12:00:00` : null,
      Month: editMonth,
      Year: editYear,
      Lines: lines,
    };
    axios({
      method: "post",
      url: config.base_url + "ChallanFeeSystem/SaveChalanEdits",
      headers: { "Content-Type": "application/json" },
      data,
      timeout: 120000,
    })
      .then((res) => {
        if (res.data.status_code !== 1) {
          const msg = res.data.status_message || "Save failed.";
          setError(msg);
          toast.error(msg, { position: toast.POSITION.TOP_RIGHT });
          return;
        }
        const msg =
          res.data.status_message ||
          "Information saved successfully. Challan totals were reconciled from fee lines.";
        setError("");
        setSaveNotice(msg);
        toast.success(msg, {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
        });
        const hRaw = pickResponseHead(res.data);
        const h = normalizeChallanHead(hRaw);
        const linesOut = pickResponseLedgerLines(res.data);
        setHead(h);
        setReadonlyLines(linesOut);
        setEditLines(cloneLines(linesOut));
        setDueDate(dueDateFromHead(h));
        setAfterDueDate(afterDueDateFromHead(h));
        const mySaved = parseHeadMonthYear(h);
        setEditMonth(mySaved.month);
        setEditYear(mySaved.year);
      })
      .catch(() => {
        const msg = "Network error while saving.";
        setError(msg);
        toast.error(msg, { position: toast.POSITION.TOP_RIGHT });
      })
      .finally(() => setSaving(false));
  };

  return (
    <>
      <FeeSystemMenu />
      <RightsCheck />
      <ToastContainer />
      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-8">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/FeeSystem/FeeSystemDashboard">Fee System</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/FeeSystem/PrintChalanList" onClick={scrollToTop}>
                        Print generated challans
                      </NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Edit challan
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Challan maintenance</div>
                <p className="text-muted small mb-0">
                  Open from monthly list (challan number) or new students list (admission). Section 1 is a read-only
                  snapshot of <code>tblADLedger</code>; section 2 edits lines, adds new fee types, and saves. Totals on
                  the challan header are recalculated when you save.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            {loading && (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            )}
            {!loading && error && !head && (
              <div className="alert alert-warning">{error}</div>
            )}
            {!loading && head && (
              <>
                {saveNotice && (
                  <div className="alert alert-success" role="status">
                    {saveNotice}
                  </div>
                )}
                {error && <div className="alert alert-danger">{error}</div>}
                <div className="card mb-3">
                  <div className="card-header">Challan header</div>
                  <div className="card-body">
                    <div className="row g-2 small">
                      <div className="col-md-3">
                        <strong>Challan no</strong>
                        <div>{head.Challan_No || "—"}</div>
                      </div>
                      <div className="col-md-3">
                        <strong>Student</strong>
                        <div>
                          {head.Student_Name} ({head.Registration_No})
                        </div>
                      </div>
                      <div className="col-md-4">
                        <strong>Challan month / year</strong>
                        {head.Is_Paid ? (
                          <div>
                            {head.Month}/{head.Year}
                          </div>
                        ) : (
                          <div className="row g-1 align-items-center mt-1">
                            <div className="col-7">
                              <select
                                className="form-select form-select-sm"
                                value={editMonth}
                                onChange={(e) =>
                                  setEditMonth(parseInt(e.target.value, 10) || 1)
                                }
                                aria-label="Challan month"
                              >
                                {CHALLAN_EDIT_MONTH_OPTIONS.map((m) => (
                                  <option key={m.value} value={m.value}>
                                    {m.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-5">
                              <select
                                className="form-select form-select-sm"
                                value={editYear}
                                onChange={(e) => {
                                  const y = parseInt(e.target.value, 10);
                                  setEditYear(Number.isFinite(y) ? y : new Date().getFullYear());
                                }}
                                aria-label="Challan year"
                              >
                                {challanEditYearRange(editYear).map((y) => (
                                  <option key={y} value={y}>
                                    {y}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="col-md-2">
                        <strong>Type</strong>
                        <div>{head.ChallanType || "—"}</div>
                      </div>
                      <div className="col-md-2">
                        <strong>Status</strong>
                        <div>
                          {head.Is_Paid ? (
                            <span className="text-success">Paid</span>
                          ) : (
                            <span className="text-danger">Unpaid</span>
                          )}
                        </div>
                      </div>
                      <div className="col-md-3">
                        <strong>Due total (head)</strong>
                        <div>{head.Due_Date_Amount}</div>
                      </div>
                      <div className="col-md-3">
                        <strong>After due total</strong>
                        <div>{head.After_Due_Date_Amount}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card mb-4">
                  <div className="card-header">
                    1 — Read-only: <code>tblADLedger</code> (as loaded)
                  </div>
                  <div className="card-body table-responsive">
                    <table className="table table-sm table-bordered align-middle">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Fee code</th>
                          <th>Description</th>
                          <th className="text-end">Fee posted</th>
                          <th className="text-end">Arrears</th>
                          <th className="text-end">Concession</th>
                          <th className="text-end">Receivable</th>
                          <th className="text-end">Received</th>
                          <th className="text-end">Pending</th>
                          <th className="text-end">Print debit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {readonlyLines.length === 0 ? (
                          <tr>
                            <td colSpan={10} className="text-muted">
                              No ledger lines
                            </td>
                          </tr>
                        ) : (
                          readonlyLines.map((row) => (
                            <tr key={row.ID}>
                              <td>{row.ID}</td>
                              <td>{row.FeeCode}</td>
                              <td>{row.Description}</td>
                              <td className="text-end">{row.Fee_Posted}</td>
                              <td className="text-end">{row.Arrears}</td>
                              <td className="text-end">{row.ConcessionAmount}</td>
                              <td className="text-end">{row.ReceiveableFee}</td>
                              <td className="text-end">{row.ReceivedAmount}</td>
                              <td className="text-end">{row.PendingFee}</td>
                              <td className="text-end">{row.DisplayDebit}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                      {readonlyLines.length > 0 ? (
                        <tfoot className="table-light">
                          <tr className="fw-semibold">
                            <td colSpan={3} className="text-end">
                              Total (as loaded)
                            </td>
                            <td className="text-end">{fmtSum(readonlyTotals.feePosted)}</td>
                            <td className="text-end">{fmtSum(readonlyTotals.arrears)}</td>
                            <td className="text-end">{fmtSum(readonlyTotals.concession)}</td>
                            <td className="text-end">{fmtSum(readonlyTotals.receivable)}</td>
                            <td className="text-end">{fmtSum(readonlyTotals.received)}</td>
                            <td className="text-end">{fmtSum(readonlyTotals.pending)}</td>
                            <td className="text-muted small">—</td>
                          </tr>
                        </tfoot>
                      ) : null}
                    </table>
                  </div>
                </div>

                <div className="card mb-4">
                  <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <span>2 — Edit challan &amp; fee lines</span>
                    {!head.Is_Paid && (
                      <button
                        type="button"
                        className="button button-white"
                        onClick={addFeeLine}
                      >
                        + Add fee line
                      </button>
                    )}
                  </div>
                  <div className="card-body">
                    {head.Is_Paid ? (
                      <p className="text-muted mb-0">Paid challans cannot be edited.</p>
                    ) : (
                      <form onSubmit={handleSave}>
                        <div className="row g-3 mb-3">
                          <div className="col-md-4">
                            <label className="form-label">Due date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={dueDate}
                              onChange={(e) => setDueDate(e.target.value)}
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">After due date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={afterDueDate}
                              onChange={(e) => setAfterDueDate(e.target.value)}
                            />
                          </div>
                        </div>
                        <p className="small text-muted">
                          New rows: choose a fee type (fills code and name), then enter amounts — at least one of
                          receivable, arrears, or fee posted (net of concession) must be billable. Pending usually
                          matches receivable for unpaid lines. Footer totals exclude rows marked for deletion and
                          reflect your current edits (compare to read-only table above).
                        </p>
                        <div className="table-responsive">
                          <table className="table table-sm table-bordered align-middle">
                            <thead>
                              <tr>
                                <th>Del</th>
                                <th>Fee type</th>
                                <th>ID</th>
                                <th>Fee code</th>
                                <th>Description</th>
                                <th className="text-end">Fee posted</th>
                                <th className="text-end">Arrears</th>
                                <th className="text-end">Concession</th>
                                <th className="text-end">Receivable</th>
                                <th className="text-end">Received</th>
                                <th className="text-end">Pending</th>
                                <th>Paid line</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {editLines.map((row, idx) => {
                                const paid =
                                  num(row.ReceivedAmount) > 0 ||
                                  (row.ReceiptRef != null && row.ReceiptRef > 0);
                                return (
                                  <tr
                                    key={rowKey(row)}
                                    className={row.DeleteLine ? "table-secondary" : ""}
                                  >
                                    <td>
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={!!row.DeleteLine}
                                        disabled={paid || row.IsNew}
                                        onChange={(e) =>
                                          updateLine(idx, "DeleteLine", e.target.checked)
                                        }
                                        title={
                                          row.IsNew
                                            ? "Remove unsaved row with ✕"
                                            : paid
                                              ? "Has receipt — cannot delete"
                                              : "Soft-delete fee line on save"
                                        }
                                      />
                                    </td>
                                    <td style={{ minWidth: "200px" }}>
                                      {row.IsNew ? (
                                        <select
                                          className="form-select form-select-sm"
                                          defaultValue=""
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            if (v) applyFeeTypeToRow(idx, v);
                                          }}
                                        >
                                          <option value="">Pick fee type…</option>
                                          {feeTypes.map((ft) => (
                                            <option key={ft.ID} value={ft.ID}>
                                              {ft.Fee_Type_Name} ({ft.Fee_Type_Code})
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <span className="text-muted small">—</span>
                                      )}
                                    </td>
                                    <td>{row.IsNew ? <em className="text-muted">New</em> : row.ID}</td>
                                    <td>
                                      <input
                                        className="form-control form-control-sm"
                                        value={row.FeeCode ?? ""}
                                        disabled={paid}
                                        onChange={(e) => updateLine(idx, "FeeCode", e.target.value)}
                                      />
                                    </td>
                                    <td>
                                      <input
                                        className="form-control form-control-sm"
                                        value={row.Description ?? ""}
                                        onChange={(e) =>
                                          updateLine(idx, "Description", e.target.value)
                                        }
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        step="0.01"
                                        className="form-control form-control-sm text-end"
                                        disabled={paid}
                                        value={row.Fee_Posted}
                                        onChange={(e) =>
                                          updateLine(idx, "Fee_Posted", e.target.value)
                                        }
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        step="0.01"
                                        className="form-control form-control-sm text-end"
                                        disabled={paid}
                                        value={row.Arrears}
                                        onChange={(e) =>
                                          updateLine(idx, "Arrears", e.target.value)
                                        }
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        step="0.01"
                                        className="form-control form-control-sm text-end"
                                        disabled={paid}
                                        value={row.ConcessionAmount}
                                        onChange={(e) =>
                                          updateLine(idx, "ConcessionAmount", e.target.value)
                                        }
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        step="0.01"
                                        className="form-control form-control-sm text-end"
                                        disabled={paid}
                                        value={row.ReceiveableFee}
                                        onChange={(e) =>
                                          updateLine(idx, "ReceiveableFee", e.target.value)
                                        }
                                      />
                                    </td>
                                    <td className="text-end text-muted small text-nowrap">
                                      {fmtSum(row.ReceivedAmount)}
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        step="0.01"
                                        className="form-control form-control-sm text-end"
                                        disabled={paid}
                                        value={row.PendingFee}
                                        onChange={(e) =>
                                          updateLine(idx, "PendingFee", e.target.value)
                                        }
                                      />
                                    </td>
                                    <td className="small text-muted">
                                      {paid ? "Yes (amounts locked)" : "No"}
                                    </td>
                                    <td>
                                      {row.IsNew ? (
                                        <button
                                          type="button"
                                          className="btn btn-link btn-sm text-danger p-0"
                                          onClick={() => removeUnsavedLine(idx)}
                                        >
                                          Remove
                                        </button>
                                      ) : null}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            {editLines.length > 0 ? (
                              <tfoot className="table-light">
                                <tr className="fw-semibold">
                                  <td colSpan={5} className="text-end">
                                    Total (current edits; excludes deleted rows)
                                  </td>
                                  <td className="text-end">{fmtSum(editTotals.feePosted)}</td>
                                  <td className="text-end">{fmtSum(editTotals.arrears)}</td>
                                  <td className="text-end">{fmtSum(editTotals.concession)}</td>
                                  <td className="text-end">{fmtSum(editTotals.receivable)}</td>
                                  <td className="text-end">{fmtSum(editTotals.received)}</td>
                                  <td className="text-end">{fmtSum(editTotals.pending)}</td>
                                  <td colSpan={2} className="text-muted small fw-normal">
                                    Compare with table 1
                                  </td>
                                </tr>
                              </tfoot>
                            ) : null}
                          </table>
                        </div>
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                          <button
                            type="submit"
                            className="button button-primary"
                            disabled={saving}
                          >
                            {saving ? "Saving…" : "Save changes"}
                          </button>
                          <button
                            type="button"
                            className="button button-white"
                            disabled={saving || reassigning}
                            onClick={() => {
                              if (
                                !window.confirm(
                                  "Assign the next challan number (YYMM + sequence + student id) and sync ledger lines? Use after edits so the bank sees a new unique number."
                                )
                              ) {
                                return;
                              }
                              setReassigning(true);
                              setError("");
                              axios({
                                method: "post",
                                url:
                                  config.base_url +
                                  "ChallanFeeSystem/ReassignChallanNumberAfterAmendment",
                                headers: { "Content-Type": "application/json" },
                                data: {
                                  AccessKey,
                                  UserID: parseInt(UserID, 10) || 0,
                                  MemberType: MemberType || "Admin",
                                  ChalanHead_Id: resolvedHeadId,
                                },
                                timeout: 60000,
                              })
                                .then((res) => {
                                  if (res.data.status_code !== 1) {
                                    setError(
                                      res.data.status_message ||
                                        "Could not reassign challan number."
                                    );
                                    toast.error(
                                      res.data.status_message ||
                                        "Could not reassign challan number.",
                                      { position: toast.POSITION.TOP_RIGHT }
                                    );
                                    return;
                                  }
                                  toast.success(
                                    res.data.status_message ||
                                      `New challan no: ${res.data.Challan_No}`,
                                    { position: toast.POSITION.TOP_RIGHT }
                                  );
                                  fetchChallan();
                                })
                                .catch(() => {
                                  setError("Network error while reassigning number.");
                                  toast.error("Network error.", {
                                    position: toast.POSITION.TOP_RIGHT,
                                  });
                                })
                                .finally(() => setReassigning(false));
                            }}
                          >
                            {reassigning ? "Reassigning…" : "New bank challan number"}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ChallanEdit;
